// Shared helpers for fetching REAL historical data from the backend's
// InfluxDB-backed history endpoints. Replaces the old per-component pattern
// of sampling the live socket value every 10s into a client-side array,
// which was not real history (empty/misleading on page load, and the time
// range buttons didn't actually change what data was fetched).
import { authHeaders } from './auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

async function getJson(url, token) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export function fetchPanelFieldHistory(token, plantId, panelId, field, range) {
  return getJson(`${API_BASE}/plants/${plantId}/panels/${panelId}/history?range=${range}&field=${field}`, token);
}

export function fetchWeatherFieldHistory(token, plantId, stationId, field, range) {
  return getJson(`${API_BASE}/plants/${plantId}/weather/${stationId}/history?range=${range}&field=${field}`, token);
}

// Active alerts (all plants the caller is authorized for) - reused by
// PlantDetailPage's "Risques météo prévus" section, which filters the
// result down to this plant's forecast-sourced alerts client-side rather
// than adding a new backend query param, exactly like AlertsPage.js already
// does its own client-side filtering over the same endpoint.
export function fetchActiveAlerts(token, limit = 100) {
  return getJson(`${API_BASE}/alerts?status=active&limit=${limit}`, token);
}

// PV production forecast (pv-v1 model) - like fetchPlantForecast, this
// returns an object (always at least {forecast_available}), not an array.
export async function fetchProductionForecast(token, plantId) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/production-forecast`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Forecast-vs-Actual comparison, built only from immutable stored snapshots
// joined against real InfluxDB production - see the backend route's comment
// for why this never re-fetches a "historical" forecast on demand.
export async function fetchProductionForecastComparison(token, plantId, days = 14) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/production-forecast/comparison?days=${days}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Real measured energy for this plant's own current local calendar day -
// generic, no environmental/financial coupling. See backend route's comment
// for the measured/no_data/query_error data_status semantics and the
// plant-timezone (falling back to UTC) day-boundary behavior.
export async function fetchPlantEnergyToday(token, plantId) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/energy-today`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Environmental impact (CO2e avoided, specific yield) - measured production
// only, no financial figures. See backend route's comment for the
// measured/no_data/query_error data_status semantics.
export async function fetchEnvironmentalImpact(token, plantId, period) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/environmental-impact?period=${period}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function fetchEnvironmentalImpactHistory(token, plantId, period) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/environmental-impact/history?period=${period}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Open-Meteo forecast for a plant - unlike the history endpoints this
// returns an object, not an array (it always carries at least
// { forecast_available }, even when false), so it can't share getJson()'s
// "always return an array" contract.
export async function fetchPlantForecast(token, plantId) {
  const res = await fetch(`${API_BASE}/plants/${plantId}/forecast`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
  });
  if (!res.ok) {
    const err = new Error(`Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Merges multiple { field: [{_time, _value}] } results into one array of
// { _time, <field1>, <field2>, ... } rows, one per distinct timestamp.
export function mergeFieldSeries(fieldResults) {
  const byTime = new Map();
  Object.entries(fieldResults).forEach(([field, rows]) => {
    (rows || []).forEach((row) => {
      const key = row._time;
      if (!byTime.has(key)) byTime.set(key, { _time: key });
      byTime.get(key)[field] = row._value;
    });
  });
  return Array.from(byTime.values()).sort((a, b) => new Date(a._time) - new Date(b._time));
}

// Sums multiple already-merged series (e.g. one per panel/station) by
// matching timestamp, for the given numeric fields. With a single source
// this is just that source's series unchanged.
export function sumMergedSeries(seriesList, fields) {
  const byTime = new Map();
  seriesList.forEach((series) => {
    series.forEach((row) => {
      const key = row._time;
      if (!byTime.has(key)) {
        const base = { _time: key };
        fields.forEach((f) => { base[f] = 0; });
        byTime.set(key, base);
      }
      const acc = byTime.get(key);
      fields.forEach((f) => { acc[f] += typeof row[f] === 'number' ? row[f] : 0; });
    });
  });
  return Array.from(byTime.values()).sort((a, b) => new Date(a._time) - new Date(b._time));
}
